if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initViewerNavArrows();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initViewerNavArrows() {
        const viewerWrap = this.querySelector('.product-media-tabs__viewer');
        if (!viewerWrap || !this.elements.viewer) return;

        this.viewerNav = {
          prevBtn: viewerWrap.querySelector('[data-gallery-nav="prev"]'),
          nextBtn: viewerWrap.querySelector('[data-gallery-nav="next"]'),
        };
        if (!this.viewerNav.prevBtn || !this.viewerNav.nextBtn) return;

        this.viewerNav.prevBtn.addEventListener('click', () => this.navigateViewer(-1));
        this.viewerNav.nextBtn.addEventListener('click', () => this.navigateViewer(1));
        this.elements.viewer.addEventListener('slideChanged', (event) => {
          if (!this.mql.matches && event.detail?.currentElement?.dataset?.mediaId) {
            const current = event.detail.currentElement;
            this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((el) => {
              el.classList.toggle('is-active', el === current);
            });
          }
          this.updateViewerNavArrows();
          this.updateSlideCounter();
        });
        this.updateViewerNavArrows();
        this.mql.addEventListener('change', () => this.updateViewerNavArrows());
        this.updateSlideCounter();
      }

      updateSlideCounter() {
        const counter = this.querySelector('[data-media-counter]');
        if (!counter) return;

        const slides = this.getViewerSlides();
        const index = slides.length > 0 ? this.getActiveViewerIndex() : 0;
        const current = counter.querySelector('[data-media-counter-current]');
        const total = counter.querySelector('[data-media-counter-total]');

        if (current) current.textContent = index + 1;
        if (total) total.textContent = slides.length;
      }

      getViewerSlides() {
        return [...this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')];
      }

      getActiveViewerIndex() {
        const slides = this.getViewerSlides();
        const activeIdx = slides.findIndex((slide) => slide.classList.contains('is-active'));
        if (activeIdx >= 0) return activeIdx;

        const slider = this.elements.viewer.querySelector('[id^="Slider-"]');
        if (!slider || slides.length < 2) return 0;

        const scrollLeft = slider.scrollLeft;
        let closest = 0;
        let minDist = Infinity;
        slides.forEach((slide, index) => {
          const dist = Math.abs(slide.offsetLeft - scrollLeft);
          if (dist < minDist) {
            minDist = dist;
            closest = index;
          }
        });
        return closest;
      }

      navigateViewer(direction) {
        const slides = this.getViewerSlides();
        if (slides.length <= 1) return;

        const currentIndex = this.getActiveViewerIndex();

        if (!this.mql.matches) {
          const nextIndex = ((currentIndex + direction) % slides.length + slides.length) % slides.length;
          const isWrapping =
            (currentIndex === slides.length - 1 && nextIndex === 0) ||
            (currentIndex === 0 && nextIndex === slides.length - 1);

          this.setActiveMedia(slides[nextIndex].dataset.mediaId, false, { instantScroll: isWrapping });
          this.updateViewerNavArrows();
          this.updateSlideCounter();
          return;
        }

        const nextIndex = currentIndex + direction;
        if (nextIndex < 0 || nextIndex >= slides.length) return;

        this.setActiveMedia(slides[nextIndex].dataset.mediaId, false);
        this.updateViewerNavArrows();
        this.updateSlideCounter();
      }

      updateViewerNavArrows() {
        if (!this.viewerNav) return;

        const slides = this.getViewerSlides();
        const { prevBtn, nextBtn } = this.viewerNav;

        if (slides.length <= 1) {
          prevBtn.disabled = true;
          nextBtn.disabled = true;
          return;
        }

        if (!this.mql.matches) {
          prevBtn.disabled = false;
          nextBtn.disabled = false;
          return;
        }

        const index = this.getActiveViewerIndex();
        prevBtn.disabled = index <= 0;
        nextBtn.disabled = index >= slides.length - 1;
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.updateViewerNavArrows();
      }

      scrollViewerToActiveMedia(activeMedia, instantScroll) {
        const slider = activeMedia.parentElement;
        if (!slider) return;

        const targetLeft = activeMedia.offsetLeft;

        if (instantScroll) {
          const previousBehavior = slider.style.scrollBehavior;
          const previousSnapType = slider.style.scrollSnapType;
          slider.style.scrollBehavior = 'auto';
          slider.style.scrollSnapType = 'none';
          slider.scrollLeft = targetLeft;
          slider.style.scrollBehavior = previousBehavior;
          slider.style.scrollSnapType = previousSnapType;
          return;
        }

        slider.scrollTo({ left: targetLeft, behavior: 'smooth' });
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        const { instantScroll = false } = options;
        const activeMedia =
          this.elements.viewer.querySelector(`.product__media-item[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('.product__media-item[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        const scrollViewer = () => {
          const isDesktopThumbnail =
            this.mql.matches && this.dataset.desktopLayout?.includes('thumbnail');

          if (!isDesktopThumbnail && (!this.mql.matches || this.elements.thumbnails)) {
            this.scrollViewerToActiveMedia(activeMedia, instantScroll);
          } else if (isDesktopThumbnail && activeMedia.parentElement) {
            activeMedia.parentElement.scrollLeft = 0;
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        };

        if (instantScroll) {
          scrollViewer();
        } else {
          window.setTimeout(scrollViewer);
        }
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) {
          this.updateViewerNavArrows();
          this.updateSlideCounter();
          return;
        }
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        this.updateViewerNavArrows();
        this.updateSlideCounter();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

if (!customElements.get('product-media-tabs')) {
  customElements.define(
    'product-media-tabs',
    class ProductMediaTabs extends HTMLElement {
      constructor() {
        super();
        this.galleries = {};
        this.sceneInitialized = false;
        this.mobileMql = window.matchMedia('(max-width: 749px)');
      }

      isMobileGallery() {
        return this.mobileMql.matches;
      }

      getLoopedIndex(index, slideCount) {
        if (slideCount <= 0) return 0;
        return ((index % slideCount) + slideCount) % slideCount;
      }

      connectedCallback() {
        this.tabs = this.querySelectorAll('[data-media-tab]');
        this.panels = this.querySelectorAll('[data-media-panel]');
        this.thumbPanels = this.querySelectorAll('[data-media-thumbs-panel]');

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', this.onTabClick.bind(this));
        });

        this.classList.remove('is-loading');
        this.initGallery('photos');

        this.waitForSwiper(() => {
          this.enhanceWithSwiper('photos');
        });

        window.addEventListener('resize', this.debounce(this.onResize.bind(this), 150));
      }

      waitForSwiper(callback, attempt = 0) {
        if (typeof Swiper !== 'undefined') {
          callback();
          return;
        }
        if (attempt > 40) return;
        window.setTimeout(() => this.waitForSwiper(callback, attempt + 1), 50);
      }

      onResize() {
        Object.values(this.galleries).forEach((gallery) => {
          this.rebuildMainSwiperIfNeeded(gallery);
          gallery.swiper?.update?.();
          gallery.thumbsSwiper?.update?.();
          this.centerThumbsSwiper(gallery);
          gallery.goToSlide?.(gallery.activeIndex ?? 0, { fromSwiper: true });
        });
      }

      shouldUseMobileLoop(gallery) {
        return this.isMobileGallery() && gallery.slides.length > 1;
      }

      rebuildMainSwiperIfNeeded(gallery) {
        if (!gallery?.swiper) return;

        const shouldLoop = this.shouldUseMobileLoop(gallery);
        const hasLoop = !!gallery.swiper.params?.loop;

        if (shouldLoop === hasLoop) return;

        const activeIndex = gallery.activeIndex ?? 0;
        gallery.swiper.destroy(true, true);
        gallery.swiper = null;
        gallery.mainEl.classList.remove('swiper-initialized', 'swiper-container-horizontal', 'swiper-preparing');
        delete gallery.mainEl.dataset.swiperInitialized;
        this.enhanceWithSwiper(gallery.type);
        gallery.goToSlide?.(activeIndex, { fromSwiper: true });
      }

      getSwiperRealIndex(swiper) {
        if (!swiper) return 0;
        if (swiper.realIndex !== undefined && swiper.params?.loop) return swiper.realIndex;
        return swiper.activeIndex ?? 0;
      }

      updateSlideCounter(gallery) {
        const counterEl = gallery.counterEl;
        if (!counterEl) return;

        const currentEl = counterEl.querySelector('[data-media-counter-current]');
        if (currentEl) currentEl.textContent = (gallery.activeIndex ?? 0) + 1;
      }

      onTabClick(event) {
        const tab = event.currentTarget;
        const target = tab.dataset.mediaTab;
        if (tab.classList.contains('is-active')) return;

        this.tabs.forEach((btn) => {
          const isActive = btn === tab;
          btn.classList.toggle('is-active', isActive);
          btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
          btn.tabIndex = isActive ? 0 : -1;
        });

        this.panels.forEach((panel) => {
          const isActive = panel.dataset.mediaPanel === target;
          panel.classList.toggle('is-active', isActive);
          panel.hidden = !isActive;
        });

        this.thumbPanels.forEach((panel) => {
          panel.classList.toggle('is-active', panel.dataset.mediaThumbsPanel === target);
        });

        if (target === 'scene') {
          this.activateGallery('scene');
        } else {
          window.requestAnimationFrame(() => {
            const gallery = this.galleries[target];
            gallery?.swiper?.update?.();
            gallery?.thumbsSwiper?.update?.();
          });
        }
      }

      activateGallery(type) {
        const panel = this.querySelector(`[data-media-panel="${type}"]`);
        if (!panel) return;

        if (!this.galleries[type]) {
          this.initGallery(type);
        }

        const startSwiper = () => {
          this.waitForSwiper(() => {
            const gallery = this.galleries[type];
            if (!gallery) return;

            gallery.slides = [...gallery.mainEl.querySelectorAll('.swiper-wrapper > .swiper-slide')];

            if (gallery.swiper && type === 'scene') {
              const needsRebuild =
                !gallery.swiper.width ||
                gallery.swiper.slides.length !== gallery.slides.length;
              if (needsRebuild) {
                gallery.swiper.destroy(true, true);
                gallery.swiper = null;
                gallery.thumbsSwiper?.destroy(true, true);
                gallery.thumbsSwiper = null;
              }
            }

            if (!gallery.swiper) {
              this.enhanceWithSwiper(type);
            }

            window.requestAnimationFrame(() => {
              gallery.swiper?.update?.();
              gallery.thumbsSwiper?.update?.();
              gallery.goToSlide?.(gallery.activeIndex ?? 0, { fromSwiper: true });
            });
          });
        };

        if (type === 'scene') {
          this.sceneInitialized = true;
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(startSwiper);
          });
          return;
        }

        startSwiper();
      }

      initGallery(type) {
        if (this.galleries[type]) return;

        const panel = this.querySelector(`[data-media-panel="${type}"]`);
        const mainEl = panel?.querySelector(`[data-swiper="${type}"]`);
        if (!mainEl) return;

        const galleryRoot = mainEl.closest('.product-media-tabs__gallery');
        const slides = [...mainEl.querySelectorAll('.swiper-wrapper > .swiper-slide')];
        const thumbsPanel = panel?.querySelector(`[data-media-thumbs-panel="${type}"]`);
        const dotsContainer = panel?.querySelector(`[data-dots="${type}"]`);
        const prevBtn = galleryRoot?.querySelector(`[data-swiper-prev="${type}"]`);
        const nextBtn = galleryRoot?.querySelector(`[data-swiper-next="${type}"]`);

        const gallery = {
          type,
          mainEl,
          slides,
          thumbsPanel,
          prevBtn,
          nextBtn,
          counterEl: galleryRoot?.querySelector(`[data-media-counter="${type}"]`),
          activeIndex: 0,
          swiper: null,
          thumbsSwiper: null,
        };

        const updateArrowState = () => {
          const slideCount = gallery.slides.length;
          const singleSlide = slideCount <= 1;

          if (prevBtn) {
            prevBtn.disabled = singleSlide || (!this.isMobileGallery() && gallery.activeIndex <= 0);
          }
          if (nextBtn) {
            nextBtn.disabled = singleSlide || (!this.isMobileGallery() && gallery.activeIndex >= slideCount - 1);
          }
        };

        const syncSlideActiveState = (index) => {
          gallery.slides.forEach((slide, slideIndex) => {
            const isActive = slideIndex === index;
            slide.classList.toggle('is-active', isActive);
            slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          });
        };

        const goToSlide = (index, options = {}) => {
          const { fromSwiper = false } = options;
          if (index < 0 || index >= gallery.slides.length) return;

          gallery.activeIndex = index;
          syncSlideActiveState(index);

          if (thumbsPanel) {
            this.updateThumbActive(thumbsPanel, index);
          }

          if (dotsContainer) {
            this.updateDotActive(dotsContainer, index);
          }

          updateArrowState();
          this.updateSlideCounter(gallery);

          if (!fromSwiper && gallery.swiper) {
            const swiperIndex = this.getSwiperRealIndex(gallery.swiper);
            if (swiperIndex !== index) {
              gallery.swiper.slideTo(index, 350);
            }
          }

          if (gallery.thumbsSwiper && gallery.thumbsSwiper.activeIndex !== index) {
            gallery.thumbsSwiper.slideTo(index);
          }
        };

        gallery.goToSlide = goToSlide;

        if (slides.length > 0) {
          const initialIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
          goToSlide(initialIndex >= 0 ? initialIndex : 0, { fromSwiper: true });
        }

        if (thumbsPanel) {
          thumbsPanel.addEventListener('click', (event) => {
            const thumb = event.target.closest('[data-thumb-index]');
            if (!thumb || !thumbsPanel.contains(thumb)) return;

            event.preventDefault();
            const index = Number.parseInt(thumb.dataset.thumbIndex, 10);
            if (Number.isNaN(index)) return;

            goToSlide(index);
          });
        }

        dotsContainer?.querySelectorAll('[data-dot-index]').forEach((dot) => {
          dot.addEventListener('click', (event) => {
            event.preventDefault();
            const index = Number.parseInt(dot.dataset.dotIndex, 10);
            if (Number.isNaN(index)) return;
            goToSlide(index);
          });
        });

        if (prevBtn) {
          prevBtn.addEventListener('click', (event) => {
            event.preventDefault();
            const slideCount = gallery.slides.length;
            if (slideCount <= 1) return;

            if (gallery.swiper?.params?.loop) {
              gallery.swiper.slidePrev();
              return;
            }

            let nextIndex = gallery.activeIndex - 1;
            if (this.isMobileGallery()) {
              nextIndex = this.getLoopedIndex(nextIndex, slideCount);
            } else if (nextIndex < 0) {
              return;
            }

            goToSlide(nextIndex);
          });
        }

        if (nextBtn) {
          nextBtn.addEventListener('click', (event) => {
            event.preventDefault();
            const slideCount = gallery.slides.length;
            if (slideCount <= 1) return;

            if (gallery.swiper?.params?.loop) {
              gallery.swiper.slideNext();
              return;
            }

            let nextIndex = gallery.activeIndex + 1;
            if (this.isMobileGallery()) {
              nextIndex = this.getLoopedIndex(nextIndex, slideCount);
            } else if (nextIndex >= slideCount) {
              return;
            }

            goToSlide(nextIndex);
          });
        }

        this.galleries[type] = gallery;
        window.requestAnimationFrame(() => this.centerThumbsSwiper(gallery));
      }

      prepareSlidesForSwiper(mainEl, slides, activeIndex = 0) {
        mainEl.classList.add('swiper-preparing');
        slides.forEach((slide, slideIndex) => {
          slide.style.removeProperty('display');
          slide.classList.toggle('is-active', slideIndex === activeIndex);
        });
      }

      markSwiperReady(mainEl) {
        mainEl.classList.add('swiper-initialized');
        mainEl.classList.remove('swiper-preparing');
      }

      centerThumbsSwiper(gallery) {
        const thumbsEl = gallery.thumbsPanel?.querySelector(`[data-swiper-thumbs="${gallery.type}"]`);
        if (!thumbsEl) return;

        const slides = thumbsEl.querySelectorAll('.swiper-slide');
        if (!slides.length) return;

        const slideWidth = 50;
        const spaceBetween = 10;
        const totalWidth = slides.length * slideWidth + Math.max(0, slides.length - 1) * spaceBetween;
        const containerWidth = gallery.thumbsPanel?.clientWidth || thumbsEl.parentElement?.clientWidth || 0;

        if (totalWidth > 0 && containerWidth > 0 && totalWidth <= containerWidth) {
          thumbsEl.classList.add('is-thumbs-centered');
          thumbsEl.style.width = `${totalWidth}px`;
        } else {
          thumbsEl.classList.remove('is-thumbs-centered');
          thumbsEl.style.removeProperty('width');
        }
      }

      createMainSwiper(gallery) {
        const { mainEl } = gallery;
        const useLoop = this.shouldUseMobileLoop(gallery);
        const swiperOptions = {
          spaceBetween: 0,
          slidesPerView: 1,
          speed: 350,
          watchOverflow: !useLoop,
          observer: true,
          observeParents: true,
          initialSlide: gallery.activeIndex ?? 0,
        };

        if (useLoop) {
          swiperOptions.loop = true;
        }

        const onSlideIndexChange = (instance) => {
          const swiper = instance.activeIndex !== undefined ? instance : instance.swiper;
          gallery.goToSlide(this.getSwiperRealIndex(swiper), { fromSwiper: true });
        };

        // Swiper 3.x callbacks
        swiperOptions.onSlideChangeEnd = onSlideIndexChange;
        // Swiper 6+ callbacks
        swiperOptions.on = {
          slideChange: onSlideIndexChange,
        };

        return new Swiper(mainEl, swiperOptions);
      }

      createThumbsSwiper(thumbsEl) {
        const thumbsOptions = {
          spaceBetween: 10,
          slidesPerView: 'auto',
          freeMode: true,
          watchSlidesProgress: true,
          preventClicks: false,
          preventClicksPropagation: false,
          observer: true,
          observeParents: true,
          breakpoints: {
            750: {
              slidesPerView: 5,
            },
          },
        };

        return new Swiper(thumbsEl, thumbsOptions);
      }

      enhanceWithSwiper(type) {
        const gallery = this.galleries[type];
        if (!gallery || gallery.swiper) return;

        gallery.slides = [...gallery.mainEl.querySelectorAll('.swiper-wrapper > .swiper-slide')];
        if (gallery.slides.length === 0) return;

        this.prepareSlidesForSwiper(gallery.mainEl, gallery.slides, gallery.activeIndex ?? 0);

        const thumbsEl = gallery.thumbsPanel?.querySelector(`[data-swiper-thumbs="${type}"]`);

        if (thumbsEl && !gallery.thumbsSwiper) {
          gallery.thumbsSwiper = this.createThumbsSwiper(thumbsEl);
          this.markSwiperReady(thumbsEl);
          this.centerThumbsSwiper(gallery);
        }

        try {
          gallery.swiper = this.createMainSwiper(gallery);
          this.markSwiperReady(gallery.mainEl);
          gallery.mainEl.dataset.swiperInitialized = 'true';

          window.requestAnimationFrame(() => {
            gallery.swiper.update?.();
            gallery.thumbsSwiper?.update?.();
            this.centerThumbsSwiper(gallery);
            gallery.goToSlide(gallery.activeIndex, { fromSwiper: true });
          });
        } catch (error) {
          this.restoreSwiperFallback(gallery);
          console.warn('Product media Swiper init failed, using fallback gallery.', error);
        }
      }

      restoreSwiperFallback(gallery) {
        if (!gallery?.mainEl) return;
        gallery.mainEl.classList.remove('swiper-preparing', 'swiper-initialized');
        gallery.thumbsPanel
          ?.querySelector('[data-swiper-thumbs]')
          ?.classList.remove('swiper-initialized', 'swiper-preparing');
        gallery.goToSlide?.(gallery.activeIndex ?? 0, { fromSwiper: true });
      }

      updateThumbActive(thumbsPanel, activeIndex) {
        thumbsPanel.querySelectorAll('[data-thumb-index]').forEach((slide) => {
          const slideIndex = Number.parseInt(slide.dataset.thumbIndex, 10);
          slide.classList.toggle('swiper-slide-thumb-active', slideIndex === activeIndex);
        });
      }

      updateDotActive(dotsContainer, activeIndex) {
        if (!dotsContainer) return;

        dotsContainer.querySelectorAll('[data-dot-index]').forEach((dot) => {
          const dotIndex = Number.parseInt(dot.dataset.dotIndex, 10);
          const isActive = dotIndex === activeIndex;
          dot.classList.toggle('is-active', isActive);
          if (isActive) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      setActiveMedia(mediaId) {
        const photosTab = this.querySelector('[data-media-tab="photos"]');
        if (photosTab && !photosTab.classList.contains('is-active')) {
          photosTab.click();
        }

        if (!this.galleries.photos) {
          this.initGallery('photos');
        }

        const gallery = this.galleries.photos;
        if (!gallery) return;

        gallery.slides = [...gallery.mainEl.querySelectorAll('.swiper-wrapper > .swiper-slide')];
        const index = gallery.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (index >= 0) {
          gallery.goToSlide(index);
        }
      }

      debounce(fn, delay) {
        let timer;
        return (...args) => {
          clearTimeout(timer);
          timer = window.setTimeout(() => fn.apply(this, args), delay);
        };
      }
    }
  );
}
